import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";

import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { AcuityConversionListener } from "@/components/site/AcuityConversionListener";
import { AcuityEmbed } from "@/components/site/AcuityEmbed";
import { clearLeadId, readLeadId, readPilot } from "@/components/forms/quick-quote-shared";
import { deliverQuickQuoteWithoutPhoto } from "@/lib/forms/quick-quote.functions";
import { getTrackingParams } from "@/lib/utm-tracking";
import bannerImg from "@/assets/restore/confirmation-team-banner.webp";

const TITLE = "Thank You - RESTORE Hair";
const DESCRIPTION =
  "Thank you for taking the first step to a new you. Our staff will be in touch shortly to schedule your complimentary RESTORE Hair consultation.";
const CANONICAL_URL = "https://restorehair.com/contact-us/confirmation-2s/";

export const Route = createFileRoute("/contact-us/confirmation-2s")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL_URL },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: CANONICAL_URL }],
  }),
  component: Confirmation2sPage,
});

const ACUITY_NATIONAL = "https://app.squarespacescheduling.com/schedule.php?owner=24213130";
// Charlotte pilot calendar (same owner): Charlotte location + Virtual.
const ACUITY_CHARLOTTE =
  "https://app.squarespacescheduling.com/schedule.php?owner=24213130&appointmentType[]=49645045&appointmentType[]=26951951";

function Confirmation2sPage() {
  const deliver = useServerFn(deliverQuickQuoteWithoutPhoto);
  const sent = useRef(false);
  const [acuitySrc, setAcuitySrc] = useState(ACUITY_NATIONAL);

  // Charlotte pilot: visitors who started on /restore-charlotte book on the
  // Charlotte calendar. Set after mount so SSR markup stays stable.
  useEffect(() => {
    if (readPilot() === "charlotte") setAcuitySrc(ACUITY_CHARLOTTE);
  }, []);

  // Finalize the buffered lead (push to Google Sheet + Salesforce) on arrival.
  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    const leadId = readLeadId();
    if (!leadId) return;
    void deliver({ data: { lead_id: leadId, tracking: getTrackingParams() } })
      .then(() => clearLeadId())
      .catch(() => {
        /* non-blocking: the lead is already buffered server-side */
      });
  }, [deliver]);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section style={{ backgroundColor: "#f5f5f5" }} className="px-4 pb-14 pt-16 md:pb-16 md:pt-[108px]">
          <div className="mx-auto max-w-[1063px]">
            <h1
              className="text-center font-bold text-black"
              style={{ fontSize: "clamp(30px, 4.4vw, 42px)", lineHeight: 1.14 }}
            >
              Thank You for Taking the 1st <br />
              Step to a New You!
            </h1>

            <p
              className="mt-8 text-center"
              style={{ fontSize: "19px", lineHeight: "32px", color: "#6a6b6c" }}
            >
              We&rsquo;re delighted that you&rsquo;re considering RESTORE&reg; to regrow your hair
              and your self-image. Thousands of satisfied clients can&rsquo;t be wrong!
            </p>
            <p
              className="mt-6 text-center"
              style={{ fontSize: "16px", lineHeight: "28px", color: "#6a6b6c" }}
            >
              Our staff will be in touch with you shortly to schedule your complimentary
              consultation. The consultation is free and there is no obligation. From there
              we&rsquo;ll give you a customized treatment plan including pricing. You may also
              schedule a time that works best for you below.
            </p>
          </div>
        </section>

        <section className="bg-white">
          <AcuityConversionListener />
          <AcuityEmbed key={acuitySrc} src={acuitySrc} initialHeight={1000} />
        </section>

        <section style={{ backgroundColor: "#f5f5f5" }} className="pt-10">
          <img
            src={bannerImg}
            alt="RESTORE Hair Transplant and Restoration team"
            width={1721}
            height={440}
            loading="lazy"
            className="block w-full"
          />
        </section>
      </main>
      <Footer />
    </div>
  );
}
